import React, { useEffect, useState } from "react";

import { settlementService } from "api/services/settlement.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";

import type { PracticePaymentRecordListItem, PracticePaymentRecord, PracticeMonthlyStats } from "utils/type/settlement";
import "pages/AdminPage.scss";
import "./PracticeSettlement.scss";

const STATUS_BADGE: Record<string, "active" | "pending" | "info" | "rejected" | "draft"> = {
  CALCULATED: "info",
  APPROVED: "pending",
  PAID: "active",
  CANCELLED: "rejected",
};

const STATUS_LABEL: Record<string, string> = {
  CALCULATED: "산출",
  APPROVED: "승인",
  PAID: "지급완료",
  CANCELLED: "취소",
};

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const PracticeSettlement: React.FC = () => {
  const [records, setRecords] = useState<PracticePaymentRecordListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState(String(currentMonth));

  // 상세 모달
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    record: PracticePaymentRecord | null;
  }>({ open: false, record: null });
  const [detailLoading, setDetailLoading] = useState(false);

  // 월별 통계
  const [stats, setStats] = useState<PracticeMonthlyStats | null>(null);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {
        year: Number(year),
        month: Number(month),
      };
      if (statusFilter) params.status = statusFilter;
      const res = await settlementService.listPracticeRecords(params);
      setRecords(Array.isArray(res.data) ? res.data : (res.data.results ?? []));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await settlementService.practiceMonthlyStats({
        year: Number(year),
        month: Number(month),
      });
      setStats(res.data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, statusFilter]);

  const openDetail = async (record: PracticePaymentRecordListItem) => {
    setDetailModal({ open: true, record: null });
    setDetailLoading(true);
    try {
      const res = await settlementService.getPracticeRecord(record.id);
      setDetailModal({ open: true, record: res.data });
    } catch {
      setDetailModal({ open: false, record: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: `${currentYear - i}년`,
  }));

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}월`,
  }));

  const columns: Column<PracticePaymentRecordListItem>[] = [
    { key: "schedule_date", title: "날짜", width: "110px" },
    { key: "schedule_title", title: "스케줄", width: "180px" },
    { key: "instructor_name", title: "강사", width: "90px" },
    {
      key: "pricing_type_display",
      title: "유형",
      width: "70px",
    },
    { key: "total_students", title: "총원", width: "60px" },
    { key: "completed_count", title: "완료", width: "60px" },
    {
      key: "total_amount",
      title: "정산금액",
      width: "110px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    {
      key: "status",
      title: "상태",
      width: "80px",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{STATUS_LABEL[String(v)] ?? String(v)}</Badge>,
    },
    {
      key: "actions",
      title: "",
      width: "80px",
      render: (_, row) => (
        <Button size="sm" variant="ghost" onClick={() => openDetail(row)}>
          상세
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="연습 정산 관리" description="연습반 정산 기록을 조회합니다." />

      {/* 월별 통계 */}
      {stats && (
        <div className="stat-cards">
          <div className="stat-card stat-card--primary">
            <div className="stat-card__label">총 세션</div>
            <div className="stat-card__value">{stats.total_sessions}</div>
          </div>
          <div className="stat-card stat-card--success">
            <div className="stat-card__label">총 정산액</div>
            <div className="stat-card__value">{Number(stats.total_amount).toLocaleString()}원</div>
          </div>
          <div className="stat-card stat-card--info">
            <div className="stat-card__label">총 수강생</div>
            <div className="stat-card__value">{stats.total_students}명</div>
          </div>
        </div>
      )}

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Select value={year} onChange={(e) => setYear(e.target.value)} options={yearOptions} />
          <Select value={month} onChange={(e) => setMonth(e.target.value)} options={monthOptions} />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "", label: "상태 전체" },
              { value: "CALCULATED", label: "산출" },
              { value: "APPROVED", label: "승인" },
              { value: "PAID", label: "지급완료" },
              { value: "CANCELLED", label: "취소" },
            ]}
          />
        </div>

        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={records} rowKey="id" />}
      </div>

      {/* 상세 모달 */}
      <Modal open={detailModal.open} title="정산 상세" width="600px" onClose={() => setDetailModal({ open: false, record: null })}>
        {detailLoading ? (
          <div className="admin-page__loading">불러오는 중...</div>
        ) : detailModal.record ? (
          <div className="settlement-detail">
            <div className="settlement-detail__grid">
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">스케줄</span>
                <span>{detailModal.record.schedule_title}</span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">날짜</span>
                <span>{detailModal.record.schedule_date}</span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">강사</span>
                <span>{detailModal.record.instructor_name}</span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">유형</span>
                <span>{detailModal.record.pricing_type_display}</span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">총원/완료</span>
                <span>
                  {detailModal.record.total_students}/{detailModal.record.completed_count}
                </span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">정산금액</span>
                <span className="settlement-detail__amount">{Number(detailModal.record.total_amount).toLocaleString()}원</span>
              </div>
              <div className="settlement-detail__item">
                <span className="settlement-detail__label">상태</span>
                <Badge variant={STATUS_BADGE[detailModal.record.status] ?? "draft"}>{detailModal.record.status_display}</Badge>
              </div>
              {detailModal.record.student_names.length > 0 && (
                <div className="settlement-detail__item settlement-detail__item--full">
                  <span className="settlement-detail__label">수강생</span>
                  <span>{detailModal.record.student_names.join(", ")}</span>
                </div>
              )}
              {detailModal.record.admin_notes && (
                <div className="settlement-detail__item settlement-detail__item--full">
                  <span className="settlement-detail__label">관리자 메모</span>
                  <span className="settlement-detail__notes">{detailModal.record.admin_notes}</span>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default PracticeSettlement;
